/**
 * DOM-based "How to Play" overlay using the shared overlay palette.
 *
 * Visual structure mirrors GameOverOverlay:
 *   ┌──────────────────────────────────────┐
 *   │  ★   TITLE   ★                       │
 *   │  ┌────────────────────────────────┐  │
 *   │  │ • SECTION HEADING              │  │
 *   │  │   body text...                 │  │
 *   │  │ ──────────────────────────────  │  │
 *   │  │ • SECTION HEADING              │  │
 *   │  │   body text...                 │  │
 *   │  └────────────────────────────────┘  │
 *   │           [   CLOSE   ]              │
 *   └──────────────────────────────────────┘
 *
 * Keeps the legacy `container` stub so existing callers
 * (`scene.container.addChild(overlay.container)`) keep working.
 *
 * @typedef {{ heading: string, body: string, color?: number }} HelpSection
 */
import { Container } from '../../vendor/pixi.min.mjs';
import { t } from '../config/i18n.js';
import { injectOverlayVars } from './overlayPalette.js';

function hex(n) {
    return '#' + Math.max(0, Math.min(0xffffff, n)).toString(16).padStart(6, '0');
}

let _stylesInjected = false;
function injectStyles() {
    if (_stylesInjected) return;
    _stylesInjected = true;
    injectOverlayVars();
    const css = `
    .hov-root { position:fixed; inset:0; z-index:9100;
      display:flex; align-items:center; justify-content:center;
      background:var(--ov-backdrop);
      font-family:'Poppins','Inter','Segoe UI',Arial,sans-serif; }
    .hov-panel { position:relative;
      background:var(--ov-panel-bg);
      border:var(--ov-panel-border-width) solid var(--ov-panel-border);
      /* Sharp corners — modal frame is square per UX direction. */
      border-radius:0;
      width:min(960px, calc(100vw - 40px));
      max-height:min(820px, calc(100vh - 40px));
      padding:34px 38px 28px;
      display:flex; flex-direction:column; gap:18px;
      box-shadow:0 24px 60px rgba(0,0,0,0.45);
      text-align:center; }
    .hov-title-row { display:flex; align-items:center; justify-content:center; gap:22px; }
    .hov-star { color:var(--ov-star); font-size:38px; line-height:1;
      filter:drop-shadow(0 2px 0 rgba(0,0,0,0.18)); }
    .hov-title { font-size:44px; font-weight:900; color:var(--ov-title);
      letter-spacing:1.4px; text-shadow:0 3px 0 var(--ov-title-shadow); }
    .hov-card { background:var(--ov-card-bg); border-radius:0;
      padding:22px 26px; text-align:left;
      flex:1 1 auto; overflow-y:auto; overflow-x:hidden;
      scrollbar-width:thin; scrollbar-color:var(--ov-accent-gold) transparent; }
    .hov-card::-webkit-scrollbar { width:8px; }
    .hov-card::-webkit-scrollbar-thumb { background:var(--ov-accent-gold); border-radius:4px; }
    .hov-card::-webkit-scrollbar-track { background:transparent; }
    .hov-section + .hov-section { margin-top:14px; padding-top:14px;
      border-top:1px solid rgba(255,255,255,0.10); }
    .hov-heading { display:flex; align-items:center; gap:10px;
      font-size:20px; font-weight:800; letter-spacing:0.5px;
      margin-bottom:6px; }
    .hov-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0;
      box-shadow:0 0 0 3px rgba(0,0,0,0.20); }
    .hov-body { color:var(--ov-label); font-size:16px; line-height:1.55;
      padding-left:22px; }
    .hov-actions { display:flex; justify-content:center; }
    .hov-btn { border:none; cursor:pointer; font-family:inherit;
      font-weight:900; letter-spacing:1px;
      background:var(--ov-btn-retry-bg); color:var(--ov-btn-retry-fg);
      border-bottom:5px solid var(--ov-btn-retry-edge);
      border-radius:0; padding:14px 42px;
      font-size:24px; min-width:200px;
      display:flex; align-items:center; justify-content:center; gap:10px;
      box-shadow:0 6px 0 rgba(0,0,0,0.18);
      transition:transform 0.12s ease, filter 0.12s ease; }
    .hov-btn:hover { transform:translateY(-2px); filter:brightness(1.05); }
    .hov-btn:active { transform:translateY(1px); box-shadow:0 3px 0 rgba(0,0,0,0.18); }
    .hov-btn svg { width:22px; height:22px; }
    `;
    const style = document.createElement('style');
    style.dataset.hov = '1';
    style.textContent = css;
    document.head.appendChild(style);
}

const CLOSE_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
    <path d="M6 6l12 12"/><path d="M18 6l-12 12"/></svg>`;

export class HelpOverlay {
    container = new Container();
    /** @type {HTMLDivElement|null} */
    _root = null;

    /**
     * @param {number} _screenW
     * @param {number} _screenH
     * @param {string} title
     * @param {HelpSection[]} sections
     * @param {() => void} onClose
     */
    show(_screenW, _screenH, title, sections, onClose) {
        injectStyles();

        const root = document.createElement('div');
        root.className = 'hov-root';

        const panel = document.createElement('div');
        panel.className = 'hov-panel';
        root.appendChild(panel);

        const titleRow = document.createElement('div');
        titleRow.className = 'hov-title-row';
        titleRow.innerHTML = `
            <span class="hov-star">★</span>
            <span class="hov-title">${escapeHtml(title)}</span>
            <span class="hov-star">★</span>
        `;
        panel.appendChild(titleRow);

        const card = document.createElement('div');
        card.className = 'hov-card';
        panel.appendChild(card);

        (sections ?? []).forEach((section) => {
            const sect = document.createElement('div');
            sect.className = 'hov-section';

            const heading = document.createElement('div');
            heading.className = 'hov-heading';
            const dotColor = hex(section.color ?? 0xfbdf34);
            heading.style.color = dotColor;
            heading.innerHTML = `<span class="hov-dot" style="background:${dotColor}"></span><span></span>`;
            heading.lastElementChild.textContent = section.heading ?? '';
            sect.appendChild(heading);

            const body = document.createElement('div');
            body.className = 'hov-body';
            body.textContent = section.body ?? '';
            sect.appendChild(body);

            card.appendChild(sect);
        });

        const actions = document.createElement('div');
        actions.className = 'hov-actions';
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'hov-btn';
        closeBtn.innerHTML = `${CLOSE_ICON_SVG}<span>${escapeHtml(t('common.close'))}</span>`;
        closeBtn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (onClose) onClose();
        });
        actions.appendChild(closeBtn);
        panel.appendChild(actions);

        document.body.appendChild(root);
        this._root = root;
    }

    destroy() {
        if (this._root) {
            this._root.remove();
            this._root = null;
        }
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this.container.removeChildren?.();
    }
}

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
